<template>
  <div class="min-h-screen bg-gray-50">
    <div class="container mx-auto px-4 py-8 max-w-7xl">
      <!-- Header with Welcome and Date -->
      <div
        class="mb-8 flex flex-col md:flex-row md:items-center md:justify-between"
      >
        <div>
          <h1 class="text-3xl font-bold text-gray-900 flex items-center">
            Welcome back, {{ currentUser.name }}
            <span
              class="ml-3 text-sm font-normal text-gray-500 bg-white px-3 py-1 rounded-full shadow-sm"
            >
              {{ currentDate }}
            </span>
          </h1>
          <p class="text-lg text-gray-600 mt-1">
            Here's what's happening with your verification tasks today
          </p>
        </div>

        <!-- Quick Actions -->
        <div class="mt-4 md:mt-0 flex items-center space-x-3">
          <button
            @click="getDashboardData"
            class="px-4 py-2 bg-white text-indigo-800 rounded-lg border border-indigo-800 hover:bg-gray-50 transition-colors flex items-center"
          >
            <svg
              class="w-4 h-4 mr-2 text-indigo-800"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
              />
            </svg>
            Refresh
          </button>
          <!-- <button
            @click="generateReport"
            class="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center"
          >
            <svg
              class="w-4 h-4 mr-2"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M9 17v-2m3 2v-4m3 4v-6m2 10H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
              />
            </svg>
            Generate Report
          </button> -->
        </div>
      </div>

      <!-- Role-based Welcome Banner -->
      <div class="rounded-xl shadow-lg p-6 mb-8 text-white bg-primary-50">
        <div class="flex items-center justify-between">
          <div class="flex items-center space-x-4">
            <div
              class="w-16 h-16 bg-white/20 rounded-xl flex items-center justify-center"
            >
              <svg
                v-if="userRole === 'Supervisor'"
                class="w-8 h-8"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
              <svg
                v-else
                class="w-8 h-8"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
                />
              </svg>
            </div>
            <div>
              <p class="text-sm opacity-90">
                {{
                  userRole === "Supervisor"
                    ? "Supervisor Dashboard"
                    : "Verification Officer Dashboard"
                }}
              </p>
              <p class="text-2xl font-bold">{{ getRoleMessage }}</p>
              <p class="text-sm opacity-90 mt-1">{{ getRoleSubMessage }}</p>
            </div>
          </div>
          <div class="hidden md:block text-right">
            <p class="text-3xl font-bold">{{ ticketsDueToday }}</p>
            <p class="text-sm opacity-90">tickets due today</p>
          </div>
        </div>
      </div>

      <!-- Key Metrics Grid -->
      <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        <!-- Total Tickets -->
        <div
          class="bg-white rounded-xl shadow-sm p-6 border border-gray-100 hover:shadow-md transition-all"
        >
          <div class="flex items-center justify-between mb-2">
            <span class="text-sm font-medium text-gray-500">Total Tickets</span>
            <span
              class="text-xs font-medium text-green-600 bg-green-50 px-2 py-1 rounded-full"
              >+12.5%</span
            >
          </div>
          <div class="flex items-end justify-between">
            <span class="text-3xl font-bold text-gray-900">{{
              formatNumber(metrics.totalTickets)
            }}</span>
            <span class="text-sm text-gray-500"
              >vs {{ formatNumber(metrics.previousTotal) }}</span
            >
          </div>
          <div class="mt-4 pt-4 border-t border-gray-100">
            <div class="flex items-center justify-between text-sm">
              <span class="text-gray-600">This week:</span>
              <span class="font-semibold text-gray-900">{{
                formatNumber(metrics.weeklyTickets)
              }}</span>
            </div>
          </div>
        </div>

        <!-- Pending Tickets -->
        <div
          class="bg-white rounded-xl shadow-sm p-6 border border-gray-100 hover:shadow-md transition-all"
        >
          <div class="flex items-center justify-between mb-2">
            <span class="text-sm font-medium text-gray-500">Pending</span>
            <span
              class="text-xs font-medium text-yellow-600 bg-yellow-50 px-2 py-1 rounded-full"
              >Needs attention</span
            >
          </div>
          <div class="flex items-end justify-between">
            <span class="text-3xl font-bold text-gray-900">{{
              formatNumber(metrics.pendingTickets)
            }}</span>
            <span class="text-sm text-gray-500"
              >{{
                ((metrics.pendingTickets / metrics.totalTickets) * 100).toFixed(
                  1
                )
              }}%</span
            >
          </div>
          <div class="mt-4 pt-4 border-t border-gray-100">
            <div class="w-full bg-gray-200 rounded-full h-2">
              <div
                class="bg-yellow-500 h-2 rounded-full"
                :style="{
                  width:
                    (metrics.pendingTickets / metrics.totalTickets) * 100 + '%',
                }"
              ></div>
            </div>
          </div>
        </div>

        <!-- In Progress -->
        <div
          class="bg-white rounded-xl shadow-sm p-6 border border-gray-100 hover:shadow-md transition-all"
        >
          <div class="flex items-center justify-between mb-2">
            <span class="text-sm font-medium text-gray-500">In Progress</span>
            <span
              class="text-xs font-medium text-blue-600 bg-blue-50 px-2 py-1 rounded-full"
              >Active</span
            >
          </div>
          <div class="flex items-end justify-between">
            <span class="text-3xl font-bold text-gray-900">{{
              formatNumber(metrics.inProgressTickets)
            }}</span>
            <span class="text-sm text-gray-500"
              >{{
                (
                  (metrics.inProgressTickets / metrics.totalTickets) *
                  100
                ).toFixed(1)
              }}%</span
            >
          </div>
          <div class="mt-4 pt-4 border-t border-gray-100">
            <div class="flex items-center justify-between text-sm">
              <span class="text-gray-600">Assigned to you:</span>
              <span class="font-semibold text-blue-600">{{
                metrics.myInProgress
              }}</span>
            </div>
          </div>
        </div>

        <!-- Completed -->
        <div
          class="bg-white rounded-xl shadow-sm p-6 border border-gray-100 hover:shadow-md transition-all"
        >
          <div class="flex items-center justify-between mb-2">
            <span class="text-sm font-medium text-gray-500">Completed</span>
            <span
              class="text-xs font-medium text-green-600 bg-green-50 px-2 py-1 rounded-full"
              >This month</span
            >
          </div>
          <div class="flex items-end justify-between">
            <span class="text-3xl font-bold text-gray-900">{{
              formatNumber(metrics.completedTickets)
            }}</span>
            <span class="text-sm text-gray-500"
              >+{{ metrics.completedThisWeek }} this week</span
            >
          </div>
          <div class="mt-4 pt-4 border-t border-gray-100">
            <div class="flex items-center justify-between text-sm">
              <span class="text-gray-600">Success rate:</span>
              <span class="font-semibold text-green-600"
                >{{ metrics.successRate }}%</span
              >
            </div>
          </div>
        </div>
      </div>

      <!-- Performance Metrics Row -->
      <div class="grid grid-cols-1 lg:grid-cols-4 gap-6 mb-8">
        <!-- Average Response Time -->
        <div
          class="bg-white rounded-xl shadow-sm p-6 border border-gray-100 col-span-1"
        >
          <div class="flex items-center justify-between mb-4">
            <span class="text-sm font-medium text-gray-500"
              >Avg. Response Time</span
            >
            <div
              class="w-8 h-8 bg-purple-50 rounded-lg flex items-center justify-center"
            >
              <svg
                class="w-4 h-4 text-purple-600"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
            </div>
          </div>
          <p class="text-2xl font-bold text-gray-900">
            {{ metrics.avgResponseTime }}
          </p>
          <p class="text-sm text-gray-500 mt-1">▼ 12% vs last week</p>
        </div>

        <!-- Verification Rate -->
        <div
          class="bg-white rounded-xl shadow-sm p-6 border border-gray-100 col-span-1"
        >
          <div class="flex items-center justify-between mb-4">
            <span class="text-sm font-medium text-gray-500"
              >Verification Rate</span
            >
            <div
              class="w-8 h-8 bg-green-50 rounded-lg flex items-center justify-center"
            >
              <svg
                class="w-4 h-4 text-green-600"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
            </div>
          </div>
          <p class="text-2xl font-bold text-gray-900">
            {{ metrics.verificationRate }}%
          </p>
          <p class="text-sm text-gray-500 mt-1">Target: 85%</p>
        </div>

        <!-- Call Attempts Today -->
        <div
          class="bg-white rounded-xl shadow-sm p-6 border border-gray-100 col-span-1"
        >
          <div class="flex items-center justify-between mb-4">
            <span class="text-sm font-medium text-gray-500"
              >Call Attempts Today</span
            >
            <div
              class="w-8 h-8 bg-blue-50 rounded-lg flex items-center justify-center"
            >
              <svg
                class="w-4 h-4 text-blue-600"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z"
                />
              </svg>
            </div>
          </div>
          <p class="text-2xl font-bold text-gray-900">
            {{ metrics.callAttemptsToday }}
          </p>
          <p class="text-sm text-gray-500 mt-1">
            {{ metrics.successfulCalls }} successful
          </p>
        </div>

        <!-- Customer Satisfaction -->
        <div
          class="bg-white rounded-xl shadow-sm p-6 border border-gray-100 col-span-1"
        >
          <div class="flex items-center justify-between mb-4">
            <span class="text-sm font-medium text-gray-500"
              >Customer Satisfaction</span
            >
            <div
              class="w-8 h-8 bg-yellow-50 rounded-lg flex items-center justify-center"
            >
              <svg
                class="w-4 h-4 text-yellow-600"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.95.69h4.915c.969 0 1.371 1.24.588 1.81l-3.976 2.888a1 1 0 00-.363 1.118l1.518 4.674c.3.922-.755 1.688-1.538 1.118l-3.976-2.888a1 1 0 00-1.176 0l-3.976 2.888c-.783.57-1.838-.197-1.538-1.118l1.518-4.674a1 1 0 00-.363-1.118l-3.976-2.888c-.784-.57-.38-1.81.588-1.81h4.914a1 1 0 00.951-.69l1.519-4.674z"
                />
              </svg>
            </div>
          </div>
          <p class="text-2xl font-bold text-gray-900">{{ metrics.csat }}%</p>
          <p class="text-sm text-gray-500 mt-1">
            Based on {{ metrics.csatReviews }} reviews
          </p>
        </div>
      </div>

      <!-- Charts Row -->
      <div class="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-8">
        <!-- Tickets Overview Chart -->
        <div class="bg-white rounded-xl shadow-sm p-6 border border-gray-100">
          <div class="flex items-center justify-between mb-6">
            <h3 class="text-lg font-semibold text-gray-900">
              Tickets Overview
            </h3>
            <div class="flex items-center space-x-4">
              <div class="flex items-center">
                <span class="w-3 h-3 bg-blue-500 rounded-full mr-1"></span>
                <span class="text-xs text-gray-500">New</span>
              </div>
              <div class="flex items-center">
                <span class="w-3 h-3 bg-yellow-500 rounded-full mr-1"></span>
                <span class="text-xs text-gray-500">In Progress</span>
              </div>
              <div class="flex items-center">
                <span class="w-3 h-3 bg-green-500 rounded-full mr-1"></span>
                <span class="text-xs text-gray-500">Completed</span>
              </div>
            </div>
          </div>

          <div class="h-64 relative">
            <!-- Chart Bars -->
            <div class="absolute inset-0 flex items-end justify-around">
              <div
                v-for="(day, index) in weeklyData"
                :key="index"
                class="flex-1 flex flex-col items-center group"
              >
                <!-- Tooltip -->
                <div
                  class="absolute bottom-full mb-2 bg-gray-900 text-white text-xs rounded-lg px-3 py-2 opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap z-10"
                >
                  {{ day.date }}<br />
                  New: {{ day.new }} | In Progress: {{ day.inProgress }} |
                  Completed: {{ day.completed }}
                </div>

                <!-- Stacked Bars -->
                <div class="w-10 flex flex-col items-center space-y-1">
                  <div
                    class="w-full bg-blue-500 rounded-t-lg transition-all duration-300"
                    :style="{ height: day.new * 2 + 'px' }"
                  ></div>
                  <div
                    class="w-full bg-yellow-500 transition-all duration-300"
                    :style="{ height: day.inProgress * 2 + 'px' }"
                  ></div>
                  <div
                    class="w-full bg-green-500 rounded-b-lg transition-all duration-300"
                    :style="{ height: day.completed * 2 + 'px' }"
                  ></div>
                </div>

                <!-- Day label -->
                <span class="text-xs text-gray-500 mt-2">{{ day.label }}</span>
              </div>
            </div>
          </div>
        </div>

        <!-- Call Outcomes Chart -->
        <div class="bg-white rounded-xl shadow-sm p-6 border border-gray-100">
          <h3 class="text-lg font-semibold text-gray-900 mb-6">
            Call Outcomes
          </h3>

          <div class="space-y-4">
            <div
              v-for="outcome in callOutcomes"
              :key="outcome.label"
              class="flex items-center"
            >
              <div class="w-24 text-sm text-gray-600">{{ outcome.label }}</div>
              <div class="flex-1 mx-4">
                <div class="relative">
                  <div class="w-full bg-gray-200 rounded-full h-2">
                    <div
                      class="h-2 rounded-full transition-all duration-500"
                      :class="outcome.colorClass"
                      :style="{ width: outcome.percentage + '%' }"
                    ></div>
                  </div>
                </div>
              </div>
              <div class="w-16 text-right">
                <span class="text-sm font-semibold text-gray-900">{{
                  outcome.count
                }}</span>
                <span class="text-xs text-gray-500 ml-1"
                  >({{ outcome.percentage }}%)</span
                >
              </div>
            </div>
          </div>

          <!-- Donut Chart Preview -->
          <div class="mt-6 pt-6 border-t border-gray-100">
            <div class="flex items-center justify-center">
              <div class="relative w-32 h-32">
                <svg
                  viewBox="0 0 100 100"
                  class="transform -rotate-90 w-full h-full"
                >
                  <!-- Background -->
                  <circle
                    cx="50"
                    cy="50"
                    r="40"
                    fill="none"
                    stroke="#f3f4f6"
                    stroke-width="12"
                  />

                  <!-- Success segment -->
                  <circle
                    cx="50"
                    cy="50"
                    r="40"
                    fill="none"
                    stroke="#10b981"
                    stroke-width="12"
                    stroke-dasharray="251.2"
                    :stroke-dashoffset="
                      251.2 - (251.2 * metrics.successfulCallsPercentage) / 100
                    "
                    stroke-linecap="round"
                  />

                  <!-- Failed segment -->
                  <circle
                    cx="50"
                    cy="50"
                    r="40"
                    fill="none"
                    stroke="#ef4444"
                    stroke-width="12"
                    stroke-dasharray="251.2"
                    :stroke-dashoffset="
                      251.2 - (251.2 * metrics.failedCallsPercentage) / 100
                    "
                    stroke-linecap="round"
                    :style="{
                      transform: `rotate(${
                        metrics.successfulCallsPercentage * 3.6
                      }deg)`,
                      transformOrigin: 'center',
                    }"
                  />
                </svg>
                <div
                  class="absolute inset-0 flex flex-col items-center justify-center"
                >
                  <span class="text-xl font-bold text-gray-900"
                    >{{ metrics.successfulCallsPercentage }}%</span
                  >
                  <span class="text-xs text-gray-500">Success</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Bottom Grid -->
      <div class="grid grid-cols-1 lg:grid-cols-3 gap-8">
        <!-- Recent Tickets -->
        <div
          class="lg:col-span-2 bg-white rounded-xl shadow-sm p-6 border border-gray-100"
        >
          <div class="flex items-center justify-between mb-6">
            <h3 class="text-lg font-semibold text-gray-900">Recent Tickets</h3>
            <router-link
              to="/tickets"
              class="text-sm text-blue-600 hover:text-blue-800 flex items-center"
            >
              View All
              <svg
                class="w-4 h-4 ml-1"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M9 5l7 7-7 7"
                />
              </svg>
            </router-link>
          </div>

          <div class="space-y-4">
            <div
              v-for="ticket in recentTickets"
              :key="ticket.id"
              class="flex items-center justify-between p-4 bg-gray-50 rounded-xl hover:bg-gray-100 transition-colors"
            >
              <div class="flex items-center space-x-4">
                <div
                  class="w-10 h-10 rounded-lg flex items-center justify-center"
                  :class="{
                    'bg-yellow-100': ticket.status === 'pending',
                    'bg-blue-100': ticket.status === 'in_progress',
                    'bg-green-100': ticket.status === 'completed',
                  }"
                >
                  <svg
                    class="w-5 h-5"
                    :class="{
                      'text-yellow-600': ticket.status === 'pending',
                      'text-blue-600': ticket.status === 'in_progress',
                      'text-green-600': ticket.status === 'completed',
                    }"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      stroke-width="2"
                      d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                    />
                  </svg>
                </div>
                <div>
                  <p class="text-sm font-medium text-gray-900">
                    {{ ticket.ticketNumber }}
                  </p>
                  <p class="text-sm text-gray-500">
                    {{ ticket.customer.name }}
                  </p>
                </div>
              </div>
              <div class="flex items-center space-x-4">
                <StatusBadge :status="ticket.status" />
                <span class="text-xs text-gray-400">{{
                  formatTimeAgo(ticket.updatedAt)
                }}</span>
              </div>
            </div>
          </div>
        </div>

        <!-- Recent Activity -->
        <div class="bg-white rounded-xl shadow-sm p-6 border border-gray-100">
          <h3 class="text-lg font-semibold text-gray-900 mb-6">
            Recent Activity
          </h3>

          <div class="space-y-4">
            <div
              v-for="activity in recentActivity"
              :key="activity.id"
              class="flex items-start space-x-3"
            >
              <div
                :class="[
                  'w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0',
                  activity.iconBg,
                ]"
              >
                <svg
                  class="w-4 h-4"
                  :class="activity.iconColor"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    :d="activity.icon"
                  />
                </svg>
              </div>
              <div class="flex-1 min-w-0">
                <p class="text-sm text-gray-900">{{ activity.message }}</p>
                <div class="flex items-center mt-1">
                  <span class="text-xs text-gray-500">{{ activity.time }}</span>
                  <span class="mx-2 text-gray-300">•</span>
                  <span class="text-xs text-gray-500">{{ activity.user }}</span>
                </div>
              </div>
            </div>
          </div>

          <button
            class="mt-6 w-full py-2 px-4 bg-gray-50 hover:bg-gray-100 rounded-lg text-sm text-gray-600 transition-colors"
          >
            View All Activity
          </button>
        </div>
      </div>

      <!-- Quick Actions Footer -->
      <!-- <div class="mt-8 grid grid-cols-1 md:grid-cols-4 gap-4">
        <button
          @click="router.push('/tickets/new')"
          class="p-4 bg-white rounded-xl shadow-sm border border-gray-100 hover:shadow-md transition-all flex items-center justify-center space-x-2 group"
        >
          <svg
            class="w-5 h-5 text-blue-600 group-hover:scale-110 transition-transform"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
              d="M12 4v16m8-8H4"
            />
          </svg>
          <span class="text-sm font-medium text-gray-700">New Ticket</span>
        </button>

        <button
          @click="router.push('/my-tickets')"
          class="p-4 bg-white rounded-xl shadow-sm border border-gray-100 hover:shadow-md transition-all flex items-center justify-center space-x-2 group"
        >
          <svg
            class="w-5 h-5 text-green-600 group-hover:scale-110 transition-transform"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
              d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
            />
          </svg>
          <span class="text-sm font-medium text-gray-700">My Tickets</span>
        </button>

        <button
          @click="router.push('/calendar')"
          class="p-4 bg-white rounded-xl shadow-sm border border-gray-100 hover:shadow-md transition-all flex items-center justify-center space-x-2 group"
        >
          <svg
            class="w-5 h-5 text-purple-600 group-hover:scale-110 transition-transform"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
              d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
            />
          </svg>
          <span class="text-sm font-medium text-gray-700">Calendar</span>
        </button>

        <button
          @click="router.push('/reports')"
          class="p-4 bg-white rounded-xl shadow-sm border border-gray-100 hover:shadow-md transition-all flex items-center justify-center space-x-2 group"
        >
          <svg
            class="w-5 h-5 text-orange-600 group-hover:scale-110 transition-transform"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
              d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"
            />
          </svg>
          <span class="text-sm font-medium text-gray-700">Reports</span>
        </button>
      </div> -->
    </div>

    <!-- Floating Action Button -->
    <button
      @click="getDashboardData"
      class="fixed bottom-6 right-6 z-50 flex h-14 w-14 items-center justify-center rounded-full bg-indigo-800 text-white shadow-lg transition-all duration-300 hover:bg-indigo-700 hover:shadow-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
      aria-label="Refresh page"
    >
      <!-- Refresh icon (SVG) -->
      <svg
        xmlns="http://www.w3.org/2000/svg"
        class="h-6 w-6"
        fill="none"
        viewBox="0 0 24 24"
        stroke="currentColor"
        :class="{ 'animate-spin': isRefreshing }"
      >
        <path
          stroke-linecap="round"
          stroke-linejoin="round"
          stroke-width="2"
          d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
        />
      </svg>
    </button>

    <div
      v-if="isRefreshing"
      class="fixed inset-0 ml-64 bg-white/80 backdrop-blur-sm flex items-center justify-center z-50"
    >
      <div class="relative">
        <!-- Outer ring -->
        <div class="w-16 h-16 border-4 border-gray-200 rounded-full"></div>
        <!-- Inner spinning gradient ring -->
        <div
          class="w-16 h-16 border-4 border-transparent rounded-full absolute top-0 left-0 border-t-indigo-500 border-r-indigo-700 animate-spin"
        ></div>
        <!-- Center dot -->
        <div
          class="w-3 h-3 bg-indigo-800 rounded-full absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2"
        ></div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from "vue";
// import { useRouter } from "vue-router";
import StatusBadge from "@/components/ui/StatusBadge.vue";
import { useAuthStore } from "@/stores/authStore";
import api from "@/utils/api";

// import { useNotifications } from "@/composables/useNotifications";

// Notification system
// const { showSuccess, showError, formatValidationErrors } = useNotifications();

const authStore = useAuthStore();

// const router = useRouter();

// User state
const currentUser = ref({});
const isRefreshing = ref(false);

if (authStore.isAuthenticated) {
  currentUser.value = authStore.user;
}

// Date
const currentDate = computed(() => {
  return new Date().toLocaleDateString("en-US", {
    weekday: "long",
    year: "numeric",
    month: "long",
    day: "numeric",
  });
});

// Role-based messages
const getRoleMessage = computed(() => {
  return currentUser.value?.role === "Supervisor"
    ? "You have 0 tickets pending assignment"
    : "You have 0 tickets in progress";
});

const getRoleSubMessage = computed(() => {
  return currentUser.value?.role === "Supervisor"
    ? "0 officers currently working"
    : "Complete 0 more to reach daily goal";
});

// Metrics data
const metrics = ref({
  totalTickets: 0,
  previousTotal: 0,
  weeklyTickets: 0,
  pendingTickets: 0,
  inProgressTickets: 0,
  myInProgress: 0,
  completedTickets: 0,
  completedThisWeek: 0,
  successRate: 0,
  avgResponseTime: "0h",
  verificationRate: 0,
  callAttemptsToday: 0,
  successfulCalls: 0,
  csat: 0,
  csatReviews: 0,
  successfulCallsPercentage: 0,
  failedCallsPercentage: 0,
});

// Tickets due today
const ticketsDueToday = ref(0);

// Weekly data for chart
const weeklyData = ref([
  // { label: "Mon", date: "Feb 24", new: 0, inProgress: 0, completed: 0 },
]);

// Call outcomes
const callOutcomes = ref([
  // {
  //   label: "Reached",
  //   count: 0,
  //   percentage: 0,
  //   colorClass: "bg-green-500",
  // },
  // {
  //   label: "Language",
  //   count: 122,
  //   percentage: 3.6,
  //   colorClass: "bg-purple-500",
  // },
  // { label: "Other", count: 100, percentage: 2.9, colorClass: "bg-gray-500" },
]);

// Recent tickets
const recentTickets = ref([
  // {
  //   id: 1,
  //   ticketNumber: "TKT-2026-0123",
  //   customer: { name: "Alice Johnson" },
  //   status: "in_progress",
  //   updatedAt: "2026-02-25T10:30:00",
  // },
]);

// Recent activity
const recentActivity = ref([
  // {
  //   id: 1,
  //   message: "Ticket #TKT-2026-0123 was updated",
  //   time: "5 minutes ago",
  //   user: "Jane Smith",
  //   icon: "M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9",
  //   iconBg: "bg-blue-100",
  //   iconColor: "text-blue-600",
  // },
]);

// Helper functions
const formatNumber = (num) => {
  return new Intl.NumberFormat().format(num);
};

const formatTimeAgo = (timestamp) => {
  const date = new Date(timestamp);
  const now = new Date();
  const diffMs = now - date;
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);

  if (diffMins < 60) {
    return `${diffMins}m ago`;
  } else if (diffHours < 24) {
    return `${diffHours}h ago`;
  } else {
    return `${diffDays}d ago`;
  }
};

// Actions
// const generateReport = () => {
//   router.push("/reports");
// };

const getDashboardData = async () => {
  isRefreshing.value = true;
  try {
    const response = await api.get("/api/v1/dashboard");
    const data = response.data.data;
    metrics.value.totalTickets = data.in_progress_count;
    // "daily_goal_progress": {
    // 	"completed": 0,
    // 	"goal": 10,
    // 	"remaining": 10,
    // 	"percentage": 0
    // },
    // "tickets_due_today": 0,
    // "total_tickets_stats": {
    // 	"total": 0,
    // 	"vs_last_month": 0,
    // 	"vs_last_month_count": 0,
    // 	"percentage_change": 0,
    // 	"trend": "up"
    // },
    // "status_breakdown": {
    // 	"pending": 0,
    // 	"needs_attention": 0,
    // 	"in_progress": 0,
    // 	"completed": 0,
    // 	"pending_percentage": 0,
    // 	"needs_attention_percentage": 0,
    // 	"in_progress_percentage": 0
    // },
    // "assigned_tickets": {
    // 	"total": 0,
    // 	"active": 0
    // },
    // "monthly_completed": {
    // 	"this_month": 0,
    // 	"this_week": 0
    // },
    // "success_rate": 0,
    // "avg_response_time": {
    // 	"hours": 0,
    // 	"trend_percentage": 0,
    // 	"trend": "down",
    // 	"comparison_text": "▼ vs last week"
    // },
    // "verification_rate": {
    // 	"rate": 0,
    // 	"target": 85,
    // 	"is_achieving_target": false
    // },
    // "call_attempts_today": {
    // 	"total": 0,
    // 	"successful": 0,
    // 	"success_rate": 0
    // },
    // "tickets_overview": [
    // 	{
    // 		"date": "Mar 2",
    // 		"day": "Mon",
    // 		"new": 0,
    // 		"in_progress": 0,
    // 		"completed": 0,
    // 		"label": "New: 0 | In Progress: 0 | Completed: 0"
    // 	},
    // 	{
    // 		"date": "Mar 3",
    // 		"day": "Tue",
    // 		"new": 0,
    // 		"in_progress": 0,
    // 		"completed": 0,
    // 		"label": "New: 0 | In Progress: 0 | Completed: 0"
    // 	},
    // 	{
    // 		"date": "Mar 4",
    // 		"day": "Wed",
    // 		"new": 0,
    // 		"in_progress": 0,
    // 		"completed": 0,
    // 		"label": "New: 0 | In Progress: 0 | Completed: 0"
    // 	},
    // 	{
    // 		"date": "Mar 5",
    // 		"day": "Thu",
    // 		"new": 0,
    // 		"in_progress": 0,
    // 		"completed": 0,
    // 		"label": "New: 0 | In Progress: 0 | Completed: 0"
    // 	},
    // 	{
    // 		"date": "Mar 6",
    // 		"day": "Fri",
    // 		"new": 0,
    // 		"in_progress": 0,
    // 		"completed": 0,
    // 		"label": "New: 0 | In Progress: 0 | Completed: 0"
    // 	},
    // 	{
    // 		"date": "Mar 7",
    // 		"day": "Sat",
    // 		"new": 0,
    // 		"in_progress": 0,
    // 		"completed": 0,
    // 		"label": "New: 0 | In Progress: 0 | Completed: 0"
    // 	},
    // 	{
    // 		"date": "Mar 8",
    // 		"day": "Sun",
    // 		"new": 0,
    // 		"in_progress": 0,
    // 		"completed": 0,
    // 		"label": "New: 0 | In Progress: 0 | Completed: 0"
    // 	}
    // ],
    // "call_outcomes": {
    // 	"success": 0,
    // 	"no_answer": 0,
    // 	"busy": 0,
    // 	"wrong_number": 0,
    // 	"cancelled": 0
    // },
    // "recent_tickets": [],
    // "recent_activity": []
  } catch (error) {
    console.log(error);
  } finally {
    isRefreshing.value = false;
  }
};

// Initialize
onMounted(() => {
  getDashboardData();
});
</script>

<style scoped>
/* Any component-specific styles can go here */
</style>
